import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Drawer,
  Divider,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
} from '@mui/material';

// Icons MUI
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import AppSearch from '../../components/base/AppSearch';

// Composant utilitaire pour les lignes de détails
const DetailItem = ({ label, value, icon, color }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {icon &&
        React.cloneElement(icon, {
          sx: { color: '#A3AED0', fontSize: '18px' },
        })}
      <Typography sx={{ color: '#A3AED0', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{ color: color || '#2B3674', fontSize: '14px', fontWeight: 700 }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const Commandes = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isIntervenant = user?.role === 3;
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const rolePath = useMemo(() => {
    // On vérifie si role_user est 1 (Super Admin) ou 2 (Admin)
    const isAdmin = user?.role === 1 || user?.role === 2;

    return isAdmin ? 'admin' : 'intervenant';
  }, [user]);

  // États pour le formulaire
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offers, setOffers] = useState([]);
  const [formData, setFormData] = useState({
    id_offre: '',
    date_cmd: '',
    email_client: '',
  });

  // 1. Définir fetchOffers AVANT l'useEffect
  const fetchOffers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/offer/g/lo/list_offer`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Utilisation de .result selon votre JSON
      setOffers(response.data.result || []);
    } catch (err) {
      console.error('Erreur offres:', err);
    }
  }, [apiUrl, rolePath, token]);

  // 2. Définir fetchOrders AVANT l'useEffect
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/${rolePath}/offer/command/l/list/list_command`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrders(response.data.result || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, rolePath]);

  // 3. Maintenant l'useEffect peut appeler les deux fonctions en toute sécurité
  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchOffers();
    }
  }, [user, fetchOrders, fetchOffers]);

  useEffect(() => {
    if (user) fetchOrders();
  }, [user, fetchOrders]);

  const handleCreateOrder = async () => {
    if (!formData.id_offre || !formData.date_cmd || !formData.email_client) {
      toast.warning('Veuillez remplir tous les champs');
      return;
    }

    try {
      setSubmitting(true);

      // Formater la date pour Laravel (remplacer le 'T' par un espace et ajouter les secondes)
      const formattedDate = formData.date_cmd.replace('T', ' ') + ':00';

      await axios.post(
        `${apiUrl}/${rolePath}/offer/command/resevration/create_command/${formData.id_offre}`,
        {
          start_time_reservation: formattedDate, // Doit correspondre au validateur Laravel
          email_client: formData.email_client,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success('Commande et réservation Zoom créées !');
      setOpenDialog(false);
      setFormData({ id_offre: '', date_cmd: '', email_client: '' });
      fetchOrders();
    } catch (err) {
      // Afficher l'erreur précise du validateur Laravel si elle existe
      const msg = err.response?.data?.message;
      toast.error(
        typeof msg === 'object'
          ? 'Erreur de validation des données'
          : msg || 'Erreur lors de la création'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const searchStr = searchTerm.toLowerCase();
      const clientFull =
        `${o.client?.nom_client} ${o.client?.prenom_client}`.toLowerCase();
      return (
        o.ref_cmd?.toLowerCase().includes(searchStr) ||
        clientFull.includes(searchStr)
      );
    });
  }, [orders, searchTerm]);

  const itemsPerPage = 8;
  const { next, currentData, currentPage, count, totalItems } = usePagination(
    filteredOrders,
    itemsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ pt: { xs: '80px', md: '20px' }, px: 3, pb: 4 }}>
      {/* --- HEADER --- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 4 }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: '800', color: '#2B3674', fontSize: '34px' }}
          >
            Gestion Commandes
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Référence, client..."
          />
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px', // Plus arrondi pour l'effet "pilule"
              px: 8, // Réduit le padding (12 était trop large)
              py: 1.5, // Ajuste le padding vertical au lieu d'une hauteur fixe
              textTransform: 'none',
              fontSize: '14px', // Taille de police standard dashboard
              fontWeight: 600, // Pour plus de lisibilité
              whiteSpace: 'nowrap', // Force le texte sur une seule ligne
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
              '&:hover': {
                bgcolor: '#3311CC',
                boxShadow: 'none',
              },
            }}
          >
            Nouvelle Commande
          </Button>
        </Stack>
      </Stack>

      {/* --- TABLEAU --- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          border: '1px solid #F4F7FE',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  RÉFÉRENCE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  CLIENT
                </TableCell>
                {/* Nouvelle Colonne */}
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  INTERVENANT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  PRODUIT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  TOTAL TTC
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  STATUT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  DATE
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#A3AED0', fontWeight: 700 }}
                >
                  ACTION
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress
                      size={30}
                      sx={{ my: 4, color: '#4318FF' }}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                currentData().map((order) => (
                  <TableRow key={order.id_cmd} hover>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 700 }}>
                      {order.ref_cmd}
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                      {isIntervenant
                        ? order.client?.prenom_client // seulement nom
                        : `${order.client?.prenom_client} ${order.client?.nom_client}`}
                    </TableCell>
                  
                    {/* Affichage de l'intervenant */}
                    <TableCell sx={{ color: '#4318FF', fontWeight: 600 }}>
                      {order.intervenant
                        ? `${order.intervenant.nom_user} ${order.intervenant.prenom_user}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                      {order.details_commandes?.[0]?.label_offre_detcmd ||
                        'Pack Video'}
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674', fontWeight: 800 }}>
                      {order.total_ttc_cmd}€
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          order.etat_cmd === 'SUCCESS' ? 'Payé' : 'En attente'
                        }
                        size="small"
                        sx={{
                          bgcolor:
                            order.etat_cmd === 'SUCCESS'
                              ? '#E6F9F0'
                              : '#FFF5F5',
                          color:
                            order.etat_cmd === 'SUCCESS'
                              ? '#05CD99'
                              : '#EE5D50',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#2B3674' }}>
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => {
                          setSelectedOrder(order);
                          setOpenDrawer(true);
                        }}
                        sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <AppPagination
          count={count}
          page={currentPage}
          onChange={next}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
        />
      </Paper>

      {/* --- DIALOGUE FORMULAIRE D'ENREGISTREMENT --- */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '15px',
            width: '100%',
            maxWidth: '500px',
            bgcolor: '#1B254B',
            color: '#fff',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>
            Formulaire d'enregistrement commande
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '1.2rem' }}>
            Sélectionnez une séance individuelle
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Sélectionnez une séance"
            value={formData.id_offre}
            onChange={(e) =>
              setFormData({ ...formData, id_offre: e.target.value })
            }
            sx={{
              mb: 3,
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': { color: '#fff' },
              '& .MuiInputLabel-root': { color: '#A3AED0' },
              '& .MuiSelect-icon': { color: '#fff' },
            }}
          >
            {offers.map((option) => (
              <MenuItem key={option.id_offre} value={option.id_offre}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {option.info_offre?.titre_offre}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#05CD99' }}>
                    {option.info_offre?.prix_offre}€ —{' '}
                    {option.info_offre?.duree_offre} min
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Typography sx={{ mb: 1, fontWeight: 600 }}>Date</Typography>
          <TextField
            fullWidth
            type="datetime-local"
            value={formData.date_cmd}
            onChange={(e) =>
              setFormData({ ...formData, date_cmd: e.target.value })
            }
            sx={{
              mb: 3,
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': { color: '#fff' },
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Typography sx={{ mb: 1, fontWeight: 600 }}>Email client</Typography>
          <TextField
            fullWidth
            placeholder="Entrez email client"
            value={formData.email_client}
            onChange={(e) =>
              setFormData({ ...formData, email_client: e.target.value })
            }
            sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': { color: '#fff' },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: 'flex-start' }}>
          <Button
            onClick={handleCreateOrder}
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: '#4A8BBE',
              '&:hover': { bgcolor: '#3a6d96' },
              textTransform: 'none',
              px: 4,
            }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Enregistrer'
            )}
          </Button>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: '#fff',
              textTransform: 'none',
              bgcolor: 'rgba(255,255,255,0.1)',
              px: 4,
              ml: 2,
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- DRAWER DÉTAILS --- */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3 } }}
      >
        {selectedOrder && (
          <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* --- HEADER FIXE --- */}
            <Box sx={{ p: 3, borderBottom: '1px solid #F4F7FE' }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    sx={{
                      color: '#A3AED0',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      mb: 0.5,
                    }}
                  >
                    Détails de la commande
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, color: '#2B3674' }}
                  >
                    {selectedOrder.ref_cmd}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setOpenDrawer(false)}
                  sx={{ bgcolor: '#F4F7FE' }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* --- CONTENU SCROLLABLE --- */}
            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              {/* Section 1: Statut Rapide */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: '16px',
                  bgcolor: '#F4F7FE',
                  border: 'none',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#A3AED0',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      État du paiement
                    </Typography>
                    <Typography sx={{ color: '#2B3674', fontWeight: 700 }}>
                      {selectedOrder.etat_cmd === 'SUCCESS'
                        ? 'Transaction Réussie'
                        : 'En attente'}
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedOrder.etat_cmd}
                    sx={{
                      bgcolor:
                        selectedOrder.etat_cmd === 'SUCCESS'
                          ? '#05CD99'
                          : '#FFB800',
                      color: '#fff',
                      fontWeight: 800,
                      borderRadius: '8px',
                    }}
                  />
                </Stack>
              </Paper>

              {/* Section 2: Informations Client */}
              <Typography
                sx={{
                  color: '#2B3674',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: '16px',
                }}
              >
                Informations Client
              </Typography>
              <Stack spacing={2} sx={{ mb: 4 }}>
                <DetailItem
  icon={<PersonIcon />}
  label="Nom complet"
  value={
    isIntervenant
      ? `${selectedOrder.client?.prenom_client}` // uniquement prénom
      : `${selectedOrder.client?.nom_client} ${selectedOrder.client?.prenom_client}`
  }
/>

                {/* Email caché pour role 3 */}
                {!isIntervenant && (
                  <DetailItem
                    icon={<InfoIcon />}
                    label="Email"
                    value={selectedOrder.client?.email_client}
                  />
                )}

                {/* Téléphone caché pour role 3 */}
                {!isIntervenant && (
                  <DetailItem
                    icon={<InfoIcon />}
                    label="Téléphone"
                    value={selectedOrder.client?.phone_client}
                  />
                )}

                <DetailItem
                  icon={<CalendarMonthIcon />}
                  label="Date de commande"
                  value={new Date(selectedOrder.created_at).toLocaleString(
                    'fr-FR'
                  )}
                />
              </Stack>

              <Divider sx={{ mb: 3 }} />
              {/* Section intervenant */}
              <Typography
                sx={{
                  color: '#2B3674',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: '16px',
                }}
              >
                Informations Intervenant
              </Typography>
              <Stack spacing={2} sx={{ mb: 4 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}
                >
                  <Avatar
                    src={`${apiUrl}/storage/workshop/${selectedOrder.intervenant?.photo_user}`}
                    sx={{ width: 50, height: 50, border: '2px solid #F4F7FE' }}
                  />
                  <Box>
                    <Typography sx={{ color: '#2B3674', fontWeight: 700 }}>
                      {selectedOrder.intervenant?.nom_user}{' '}
                      {selectedOrder.intervenant?.prenom_user}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#A3AED0' }}>
                      {selectedOrder.intervenant?.title_user}
                    </Typography>
                  </Box>
                </Box>
                <DetailItem
                  icon={<InfoIcon />}
                  label="Spécialité"
                  value={selectedOrder.intervenant?.main_speciality_user}
                />
                <DetailItem
                  icon={<InfoIcon />}
                  label="Email pro"
                  value={selectedOrder.intervenant?.email_user}
                />
                <DetailItem
                  icon={<InfoIcon />}
                  label="Commission"
                  value={`${selectedOrder.intervenant?.commission}%`}
                />
              </Stack>

              <Divider sx={{ mb: 3 }} />
              {/* Section 3: Articles & Offres */}
              <Typography
                sx={{
                  color: '#2B3674',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: '16px',
                }}
              >
                Articles commandés
              </Typography>
              {selectedOrder.details_commandes?.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: '12px',
                    border: '1px solid #F4F7FE',
                    position: 'relative',
                  }}
                >
                  <Typography
                    sx={{ color: '#2B3674', fontWeight: 700, fontSize: '14px' }}
                  >
                    {item.label_offre_detcmd}
                  </Typography>
                  <Typography sx={{ color: '#A3AED0', fontSize: '12px' }}>
                    Type: {item.type_detail} | Qté: {item.qnt_offre_detcmd}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4318FF',
                      fontWeight: 800,
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {item.pu_offre_detcmd}€
                  </Typography>
                </Box>
              ))}

              {/* Section 4: Totaux Financiers */}
              <Box
                sx={{
                  mt: 4,
                  p: 2,
                  bgcolor: '#2B3674',
                  borderRadius: '16px',
                  color: '#fff',
                }}
              >
                <Stack spacing={1}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ opacity: 0.8, fontSize: '14px' }}>
                      Total Hors Taxe
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {Number(selectedOrder.total_htc_cmd).toFixed(3)}€
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ opacity: 0.8, fontSize: '14px' }}>
                      TVA (20%)
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {Number(selectedOrder.total_tva_cmd).toFixed(3)}€
                    </Typography>
                  </Box>
                  <Divider
                    sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>
                      Total TTC
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '22px',
                        color: '#05CD99',
                      }}
                    >
                      {selectedOrder.total_ttc_cmd}€
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Bouton d'action facultatif */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                sx={{
                  mt: 3,
                  borderRadius: '12px',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#4318FF',
                  borderColor: '#4318FF',
                }}
              >
                Télécharger la facture (PDF)
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default Commandes;
